import {
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Badge,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "../utils/axios-instance";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { ServerRoutes } from "../routes";
import { SaleReport } from "../types";
import { useQuery } from "@tanstack/react-query";
import FilterListIcon from "@mui/icons-material/FilterList";

// Fetch Sale Report Function
const fetchSaleReport = async (
  fromDate?: Dayjs | null,
  toDate?: Dayjs | null
) => {
  const query = new URLSearchParams();
  if (fromDate) query.append("from", fromDate.format("YYYY-MM-DD"));
  if (toDate) query.append("to", toDate.format("YYYY-MM-DD"));

  const response = await axios.get<SaleReport[]>(
    `${ServerRoutes.saleReport}?${query.toString()}`
  );

  // Check if response is actually an array
  if (!Array.isArray(response.data)) {
    throw new Error("Invalid data format: expected an array");
  }

  return response.data;
};

export function SaleReportSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  
  // Filter modal state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Dayjs | null>(null);
  const [tempToDate, setTempToDate] = useState<Dayjs | null>(null);

  const {
    data: saleReport,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<SaleReport[], Error>({
    queryKey: ["saleReport", fromDate?.format(), toDate?.format()],
    queryFn: () => fetchSaleReport(fromDate, toDate),
  });

  const openFilterModal = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const applyFilters = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    closeFilterModal();
    refetch();
  };

  const clearFilters = () => {
    setTempFromDate(null);
    setTempToDate(null);
  };

  const hasActiveFilters = fromDate || toDate;
  
  // Calculate the number of active filters for badge
  const activeFilterCount = [
    fromDate ? 1 : 0,
    toDate ? 1 : 0
  ].reduce((sum, value) => sum + value, 0);

  // Handle removing filters directly from chips
  const handleRemoveFromDate = () => {
    setFromDate(null);
    refetch();
  };

  const handleRemoveToDate = () => {
    setToDate(null);
    refetch();
  };

  return (
    <Card 
      sx={{ 
        p: { xs: 2, sm: 4 },
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        background: 'white',
      }}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <Box 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"} 
          justifyContent="space-between" 
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 2 : 0}
        >
          <Typography variant="h5" fontWeight="bold" color="text.secondary">
            Sale Report
          </Typography>
          <Badge 
            badgeContent={activeFilterCount} 
            color="primary" 
            invisible={!hasActiveFilters}
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 'bold',
              }
            }}
          >
            <Button
              startIcon={<FilterListIcon />}
              variant={hasActiveFilters ? "contained" : "outlined"}
              color="primary"
              onClick={openFilterModal}
              sx={{ 
                height: '40px',
                borderRadius: 1.5,
              }}
            >
              Filters
            </Button>
          </Badge>
        </Box>

        {hasActiveFilters && (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {fromDate && (
              <Chip 
                label={`From: ${fromDate.format('MMM DD, YYYY')}`} 
                color="primary" 
                variant="outlined" 
                size="small" 
                onDelete={handleRemoveFromDate}
              />
            )}
            {toDate && (
              <Chip 
                label={`To: ${toDate.format('MMM DD, YYYY')}`} 
                color="primary" 
                variant="outlined" 
                size="small" 
                onDelete={handleRemoveToDate}
              />
            )}
          </Stack>
        )}

        <Paper 
          elevation={0} 
          sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {isError ? (
            <Box p={3} textAlign="center">
              <Typography color="error" variant="body1">
                🚨 {error?.message}
              </Typography>
            </Box>
          ) : isLoading ? (
            <Box p={2}>
              {[...Array(4)].map((_, index) => (
                <Box key={index} display="flex" gap={2} py={2} px={2}>
                  <Skeleton variant="text" width="20%" height={30} />
                  <Skeleton variant="text" width="30%" height={30} />
                  <Skeleton variant="text" width="20%" height={30} />
                  <Skeleton variant="text" width="20%" height={30} />
                </Box>
              ))}
            </Box>
          ) : Array.isArray(saleReport) && saleReport.length > 0 ? (
            <Box sx={{ 
              width: '100%',
              overflowX: 'auto',
              '& .MuiTable-root': {
                minWidth: isMobile ? '800px' : '100%',
              }
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    {[
                      "ID",
                      "Product Name",
                      "Sale",
                      "Commission",
                    ].map((value) => (
                      <TableCell
                        key={value}
                        sx={{ 
                          fontWeight: 600, 
                          color: 'text.secondary',
                          borderBottom: '1px solid #e0e0e0',
                          minWidth: '150px',
                        }}
                      >
                        {value}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {saleReport.map((report, index) => (
                    <TableRow
                      key={report.productId}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      <TableCell sx={{ minWidth: '150px' }}>{report.productId}</TableCell>
                      <TableCell sx={{ minWidth: '150px' }}>{report.productName}</TableCell>
                      <TableCell sx={{ minWidth: '150px' }}>{report.totalAmount}</TableCell>
                      <TableCell sx={{ minWidth: '150px' }}>{report.totalCommission}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No sale reports found.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Filter Modal */}
      <Dialog 
        open={isFilterModalOpen} 
        onClose={closeFilterModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Filter Sale Report
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Date Range
            </Typography>
            <Box display="flex" gap={2} flexDirection={isMobile ? "column" : "row"}>
              <DatePicker
                label="From Date"
                value={tempFromDate}
                onChange={(date) => setTempFromDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="To Date"
                value={tempToDate}
                onChange={(date) => setTempToDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={clearFilters}
            color="inherit"
          >
            Clear All
          </Button>
          <Button onClick={closeFilterModal} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={applyFilters} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
